import { X, Lock, ShieldCheck, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { BuyButton } from "./BuyButton";
import { PaymentBadges } from "./TrustBadges";
import { useShop } from "@/lib/shop";

const UPSELL = {
  ebook: { role: "workbook", title: "Add the Workbook", text: "Apply every chapter with guided worksheets and templates." },
  workbook: { role: "ebook", title: "Add the eBook", text: "The Workbook is built around the Blueprint. Get the method behind it." },
};

const Line = ({ line }) => {
  const { updateLine, roleOfLine, setCartOpen, money } = useShop();
  const m = line.merchandise;
  const plan = line.sellingPlanAllocation?.sellingPlan?.name;
  const role = roleOfLine(line);
  const amount = money(line.cost?.totalAmount?.amount ?? 0, line.cost?.totalAmount?.currencyCode);
  return (
    <li className="flex gap-4 border-b border-line py-5" data-testid="cart-line">
      <img src={m?.product?.featuredImage?.url ? `${m.product.featuredImage.url}&width=200` : ""} alt="" className="h-16 w-24 shrink-0 border border-line object-cover" />
      <div className="flex flex-1 flex-col justify-between">
        <div className="flex justify-between gap-3">
          <div>
            {role ? (
              <Link to={`/products/${role}`} onClick={() => setCartOpen(false)} data-testid="cart-line-title" className="link-u font-display text-sm text-white">{m?.product?.title}</Link>
            ) : <p className="font-display text-sm text-white">{m?.product?.title}</p>}
            {plan && <p className="mt-1 text-xs text-zinc-400">{plan.trim()}: {amount} today, the same amount in 1 month</p>}
          </div>
          <button onClick={() => updateLine(line.id, 0)} aria-label="Remove" data-testid="cart-line-remove" className="text-zinc-400 hover:text-white"><X size={16} /></button>
        </div>
        <div className="mt-2 flex items-center justify-end">
          <span className="font-display text-sm text-white">{amount}</span>
        </div>
      </div>
    </li>
  );
};

const Upsell = ({ roles }) => {
  const { products, money } = useShop();
  if (roles.includes("bundle")) return null;
  const base = roles.length === 1 ? UPSELL[roles[0]] : null;
  if (!base) return null;
  const p = products[base.role];
  return (
    <div className="mt-6 border border-cta bg-[#0a0f1f] p-5" data-testid="cart-upsell">
      <p className="eyebrow">Recommended</p>
      <div className="mt-3 flex gap-4">
        <img src={p.image} alt={p.name} className="h-16 w-24 shrink-0 object-cover" />
        <div>
          <p className="font-display text-white">{base.title} <span className="text-zinc-300">+{money(p.price, p.currency)}</span></p>
          <p className="mt-1 text-xs leading-relaxed text-zinc-300">{base.text}</p>
        </div>
      </div>
      <BuyButton role={base.role} testId="cart-upsell-add" className="mt-4 w-full !bg-transparent border border-white/40 !py-3 text-sm hover:!bg-white/5">Add to order</BuyButton>
    </div>
  );
};

const Progress = ({ roles }) => {
  const { products, money } = useShop();
  const goal = products.bundle.price;
  const done = roles.includes("bundle") || (roles.includes("ebook") && roles.includes("workbook"));
  const have = done ? goal : roles.reduce((s, r) => s + (products[r]?.price ?? 0), 0);
  const pct = Math.min(100, Math.round((have / goal) * 100));
  const left = Math.max(0, goal - have);
  return (
    <div className="border-b border-line py-5" data-testid="cart-progress">
      <p className="text-sm text-zinc-200" data-testid="cart-progress-text">
        {done ? "You have the complete First Mover system." : <>Only <span className="font-display text-white">{money(left, products.bundle.currency)}</span> away from the complete system</>}
      </p>
      <div className="mt-3 h-1.5 w-full bg-[#1f1f1f]">
        <div className="h-full bg-cta transition-[width] duration-700 ease-out" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-2 flex justify-between text-[11px] uppercase tracking-widest text-zinc-400">
        <span>eBook</span><span>Workbook</span><span>Complete</span>
      </div>
    </div>
  );
};

export const CartDrawer = () => {
  const { cart, cartOpen, setCartOpen, checkout, roleOfLine, money } = useShop();
  const lines = Array.isArray(cart?.lines?.nodes) ? cart.lines.nodes : [];
  const roles = lines.map(roleOfLine).filter(Boolean);
  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent side="right" data-lenis-prevent className="flex w-full flex-col border-l border-line bg-[#080808] p-0 text-white sm:max-w-md" data-testid="cart-drawer">
        <div className="border-b border-line px-6 py-5">
          <SheetTitle className="font-display text-xl text-white">Your cart</SheetTitle>
          <SheetDescription className="text-xs text-zinc-400">Instant download after payment</SheetDescription>
        </div>
        <div className="flex-1 overflow-y-auto px-6">
          {lines.length === 0 ? (
            <p className="py-16 text-center text-zinc-400" data-testid="cart-empty">Your cart is empty.</p>
          ) : (
            <>
              <Progress roles={roles} />
              <ul>{lines.map((l) => <Line key={l.id} line={l} />)}</ul>
              <Upsell roles={roles} />
            </>
          )}
        </div>
        {lines.length > 0 && (
          <div className="border-t border-line px-6 py-6">
            <div className="flex items-baseline justify-between">
              <span className="text-zinc-300">{lines.some((l) => l.sellingPlanAllocation) ? "Due today" : "Total"}</span>
              <span className="font-display text-2xl text-white" data-testid="cart-total">{money(cart?.cost?.totalAmount?.amount ?? 0, cart?.cost?.totalAmount?.currencyCode)}</span>
            </div>
            <p className="mt-4 flex items-start gap-2 border border-line bg-white/[0.03] px-3 py-2.5 text-xs leading-relaxed text-zinc-300" data-testid="cart-checkout-note">
              <Mail size={14} className="mt-0.5 shrink-0 text-[#6b9bff]" />
              <span>Next: pay securely with Shopify. Your download link arrives by email instantly.</span>
            </p>
            <button onClick={checkout} data-testid="cart-checkout-button" className="btn-cta mt-4 w-full"><Lock size={16} /> Secure checkout</button>
            <p className="mt-4 flex items-center justify-center gap-2 text-xs text-zinc-300"><ShieldCheck size={14} /> 7-day money-back guarantee</p>
            <PaymentBadges className="mt-4 flex flex-col items-center" />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export const CartButton = () => {
  const { cart, setCartOpen } = useShop();
  const n = cart?.totalQuantity ?? 0;
  return (
    <button onClick={() => setCartOpen(true)} aria-label="Open cart" data-testid="header-cart-button" className="relative p-2 text-white">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M6 7h12l-1 13H7L6 7Z" /><path d="M9 7a3 3 0 0 1 6 0" /></svg>
      {n > 0 && <span data-testid="header-cart-count" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center bg-cta px-1 text-[10px] font-semibold text-white">{n}</span>}
    </button>
  );
};
